/**
 * Some in-app browsers have no Web Storage at all: Apple Mail's link preview
 * throws "Can't find variable: localStorage" (Sentry POCKETSFLOW-FRONTEND-9K),
 * and blocked-storage modes throw on access. The app, and auth0-spa-js's
 * token cache, read `localStorage` / `sessionStorage` directly, so this puts
 * an in-memory Storage in place before anything else runs. Values then last
 * for the page load only; Auth0 keeps its login transaction in cookies
 * instead (see `hasPersistentStorage`).
 */

type StorageName = "localStorage" | "sessionStorage";

const works = (win: Window, name: StorageName): boolean => {
	try {
		const storage = win[name];
		if (!storage) return false;
		const probe = "__pf_storage_probe__";
		storage.setItem(probe, probe);
		storage.removeItem(probe);
		return true;
	} catch {
		return false;
	}
};

/** A Storage kept in memory. Items are own enumerable keys, like the real one. */
export const createMemoryStorage = (): Storage => {
	const items = new Map<string, string>();
	const storage = {} as Storage;
	const removeItem = (key: string) => {
		const name = String(key);
		if (!items.delete(name)) return;
		delete (storage as unknown as Record<string, unknown>)[name];
	};
	const methods = {
		getItem: (key: string) => items.get(String(key)) ?? null,
		setItem: (key: string, value: string) => {
			const name = String(key);
			items.set(name, String(value));
			Object.defineProperty(storage, name, {
				value: String(value),
				enumerable: true,
				configurable: true,
				writable: true,
			});
		},
		removeItem: (key: string) => removeItem(key),
		clear: () => {
			for (const key of [...items.keys()]) removeItem(key);
		},
		key: (index: number) => [...items.keys()][index] ?? null,
	};
	for (const [name, value] of Object.entries(methods)) {
		Object.defineProperty(storage, name, { value, enumerable: false });
	}
	Object.defineProperty(storage, "length", {
		get: () => items.size,
		enumerable: false,
	});
	return storage;
};

let persistent = true;

/** False when this browser had no working localStorage at boot. */
export const hasPersistentStorage = (): boolean => persistent;

export const installStorageFallback = (win: Window = window): void => {
	for (const name of ["localStorage", "sessionStorage"] as const) {
		if (works(win, name)) continue;
		if (name === "localStorage") persistent = false;
		try {
			Object.defineProperty(win, name, {
				value: createMemoryStorage(),
				configurable: true,
				writable: true,
			});
		} catch {
			// Not replaceable here: the guarded call sites still cope.
		}
	}
};

if (typeof window !== "undefined") installStorageFallback();
